import type { ViewportPort } from '@elara/client-core/lib/viewport-port';

/**
 * Web `ViewportPort` implementation backed by `window` (ELARA-754).
 *
 * Reads `window.innerWidth` and maps viewport-size changes onto the `resize` event. A missing global
 * (SSR, hardened/locked-down contexts) degrades to a wide default width and a no-op subscription
 * rather than throwing, so callers can wire the port unconditionally — the wide default keeps the
 * `isMobile` derivation `false` when the viewport can't be measured.
 *
 * This file is web-only and must not be imported by `@elara/mobile`; React Native composes its own
 * `Dimensions`-backed `ViewportPort` instead.
 */

/** Fallback width used when `window` is unavailable — wide enough to read as a non-mobile layout. */
const FALLBACK_WIDTH = 1024;

export function createWebViewportPort(): ViewportPort {
  return {
    getWidth() {
      if (typeof window === 'undefined') return FALLBACK_WIDTH;
      return window.innerWidth;
    },
    subscribe(listener) {
      if (typeof window === 'undefined') return () => {};
      window.addEventListener('resize', listener);
      return () => {
        window.removeEventListener('resize', listener);
      };
    },
  };
}
